import React from 'react';
import {
  AppButton,
  AppCheckbox,
  AppRadio,
  AppRadioGroup,
  AppSelect,
  AppTextField,
} from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import { RA2NDatePicker } from '@/components/organisms';
import { BUI_OPTIONS } from '../constants';
import type { SearchState, SearchTermType } from '../types';
import { toSearchTextType, toSortValue } from '../utils';

interface Option {
  value: string;
  label: string;
}

interface Props {
  search: SearchState;
  termType: SearchTermType;
  rateOptions: Option[];
  iniOptions: Option[];
  updateSearch: <K extends keyof SearchState>(key: K, value: SearchState[K]) => void;
  setSearch: React.Dispatch<React.SetStateAction<SearchState>>;
  onTermTypeChange: (value: SearchTermType) => void;
  onSearch: () => void;
}

const SearchFilters: React.FC<Props> = ({
  search,
  termType,
  rateOptions,
  iniOptions,
  updateSearch,
  setSearch,
  onTermTypeChange,
  onSearch,
}) => {
  return (
    <CollapsibleCard title="[来院用]領収書(窓口)" className="mb-4">
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-x-6 gap-y-3">
          <div className="flex items-center gap-3">
            <AppRadio
              className="text-sm whitespace-nowrap min-w-[96px]"
              checked={termType === 1}
              onChange={() => onTermTypeChange(1)}
            >
              日別
            </AppRadio>
            <RA2NDatePicker
              value={search.search_treatment || undefined}
              onChange={v => updateSearch('search_treatment', v || '')}
              placeholder="元号00年00月00日"
              picker="day"
              disabled={termType !== 1}
            />
          </div>
          <div className="flex items-center gap-3">
            <AppRadio
              className="text-sm whitespace-nowrap min-w-[96px]"
              checked={termType === 2}
              onChange={() => onTermTypeChange(2)}
            >
              月別
            </AppRadio>
            <RA2NDatePicker
              value={search.search_treatment_yearmonth || undefined}
              onChange={v => updateSearch('search_treatment_yearmonth', v || '')}
              placeholder="元号00年00月"
              picker="month"
              disabled={termType !== 2}
            />
          </div>
          <div className="flex items-center gap-3">
            <AppRadio
              className="text-sm whitespace-nowrap min-w-[96px]"
              checked={termType === 3}
              onChange={() => onTermTypeChange(3)}
            >
              施術期間
            </AppRadio>
            <RA2NDatePicker
              value={search.search_treatement_from || undefined}
              onChange={v => updateSearch('search_treatement_from', v || '')}
              placeholder="元号00年00月00日"
              picker="day"
              disabled={termType !== 3}
            />
            <span className="text-center text-gray-500">〜</span>
            <RA2NDatePicker
              value={search.search_treatement_to || undefined}
              onChange={v => updateSearch('search_treatement_to', v || '')}
              placeholder="元号00年00月00日"
              picker="day"
              disabled={termType !== 3}
            />
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <span className="text-sm">負担割合</span>
            <AppSelect
              className="w-[120px]"
              value={search.patient_cost_rate}
              onChange={v => updateSearch('patient_cost_rate', String(v ?? ''))}
              options={rateOptions}
              disabled={termType !== 3}
            />
            <span className="text-sm">部位数</span>
            <AppSelect
              className="w-[120px]"
              value={search.bui}
              onChange={v => updateSearch('bui', String(v ?? ''))}
              options={BUI_OPTIONS}
              disabled={termType !== 3}
            />
            <AppCheckbox
              className="text-sm"
              checked={search.shoken1}
              onChange={e => {
                const checked = e.target.checked;
                setSearch(prev => ({
                  ...prev,
                  shoken1: checked,
                  shoken2: checked ? false : prev.shoken2,
                }));
              }}
              disabled={termType !== 3}
            >
              初検のみ
            </AppCheckbox>
            <AppCheckbox
              className="text-sm"
              checked={search.shoken2}
              onChange={e => {
                const checked = e.target.checked;
                setSearch(prev => ({
                  ...prev,
                  shoken2: checked,
                  shoken1: checked ? false : prev.shoken1,
                }));
              }}
              disabled={termType !== 3}
            >
              初検を除く
            </AppCheckbox>
          </div>
        </div>

        <hr className="border-gray-200" />

        <div className="grid grid-cols-1 gap-3 xl:grid-cols-[1fr_auto] items-end">
          <div className="flex flex-wrap gap-3 items-center">
            <AppRadioGroup
              value={search.search_text}
              onChange={e => updateSearch('search_text', toSearchTextType(e.target.value))}
              className="flex flex-wrap items-center gap-4"
            >
              <AppRadio className="text-sm" value={1}>
                受診者番号
              </AppRadio>
              <AppTextField
                className="w-[90px]"
                value={search.search_patient_id}
                maxLength={225}
                onChange={e => updateSearch('search_patient_id', e.target.value.slice(0, 225))}
                disabled={search.search_text !== 1}
              />
              <AppRadio className="text-sm" value={2}>
                受診者名
              </AppRadio>
              <AppTextField
                className="w-[140px]"
                value={search.search_patient_name}
                maxLength={225}
                onChange={e => updateSearch('search_patient_name', e.target.value.slice(0, 225))}
                disabled={search.search_text !== 2}
              />
              <AppRadio className="text-sm" value={3}>
                仮名頭文字
              </AppRadio>
              <AppSelect
                className="w-[120px]"
                value={search.ini}
                onChange={v => updateSearch('ini', String(v ?? ''))}
                options={iniOptions}
                disabled={search.search_text !== 3}
              />
            </AppRadioGroup>
          </div>

          <div className="flex items-center gap-4">
            <AppRadioGroup
              value={search.sort}
              onChange={e => updateSearch('sort', toSortValue(e.target.value))}
              className="flex items-center gap-3"
            >
              <AppRadio value={0}>受診者番号順</AppRadio>
              <AppRadio value={1}>仮名順</AppRadio>
              <AppRadio value={2}>入力順</AppRadio>
            </AppRadioGroup>
            <AppButton type="primary" onClick={onSearch}>
              <i className="fa fa-search mr-1" aria-hidden /> 検索
            </AppButton>
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
};

export default SearchFilters;
